import React, { useState, useEffect } from "react";
import countryData from "../data/country.json";
import { db, auth } from "../firebase";
import { query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { addDoc, collection } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const AddGlobalExchangeModal = () => {
  const [formData, setFormData] = useState({
    receiverCountry: "",
    amount: "1",           // Fixed USD amount = 1
    fxAmount: "",
    status: "Active",
  });

  const [userId, setUserId] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setUserId(user.uid);
      else setUserId(null);
    });
    return () => unsubscribe();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit form to Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      alert("User not authenticated.");
      return;
    }

    const selectedCountry = countryData.find(
      (c) => c.name === formData.receiverCountry
    );

    if (!selectedCountry) {
      alert("Please select a valid country.");
      return;
    }

    if (!formData.fxAmount || Number(formData.fxAmount) <= 0) {
      alert("Please enter a valid Exchange Amount.");
      return;
    }

    try {
      const payload = {
        amount: 1, // fixed USD amount
        currencyCode: "USD",
        fxAmount: parseFloat(formData.fxAmount),
        isoCode: selectedCountry.isoCode,
        receiverCountry: formData.receiverCountry,
        receiverCurrency: selectedCountry.currency || "Local Currency",
        status: formData.status.toLowerCase(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Query to find existing document with the same isoCode
      const q = query(
        collection(db, "globalExchange"),
        where("isoCode", "==", selectedCountry.isoCode)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // If exists, update the first matching doc (assuming unique isoCode)
        const existingDoc = querySnapshot.docs[0];
        const docRef = doc(db, "globalExchange", existingDoc.id);

        await updateDoc(docRef, payload);
        console.log("Updated existing exchange rate for", selectedCountry.isoCode);
      } else {
        // If not exists, add new doc without uid
        const docRef = await addDoc(collection(db, "globalExchange"), payload);

        // Then update uid field with the newly created doc's id
        await updateDoc(docRef, { uid: docRef.id });

        console.log("Added new exchange rate for", selectedCountry.isoCode);
      }

      setSuccessMsg("Global exchange rate saved successfully!");
      setFormData({
        receiverCountry: "",
        amount: "1",
        fxAmount: "",
        status: "Active",
      });

      setTimeout(() => setSuccessMsg(""), 3000);
      window.location.reload();

    } catch (error) {
      console.error("Error saving global exchange:", error);
      alert("Failed to save exchange rate.");
    }

  };

  // Get currency code of selected country or fallback text
  const selectedCurrencyCode =
    countryData.find((c) => c.name === formData.receiverCountry)?.currency ||
    "";

  return (
    <div
      className="modal fade"
      id="addGlobalExchangeModal"
      tabIndex="-1"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header border-0">
            <h5 className="text-h1 fw-bold text-primary w-100 text-center mt-3">
              Add Global Exchange Rate
            </h5>
            <button
              type="button"
              className="btn-close position-absolute end-0 top-0 m-3"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          <div className="modal-body px-4 pb-3">
            {successMsg && (
              <div className="alert alert-success">{successMsg}</div>
            )}
            <form onSubmit={handleSubmit}>
              {/* Country Dropdown */}
              <div className="mb-2">
                <label className="form-label fw-semibold">Receiver Country</label>
                <select
                  name="receiverCountry"
                  className="form-select"
                  value={formData.receiverCountry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Country</option>
                  {countryData.map((country, idx) => (
                    <option key={idx} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* USD Amount (fixed 1) */}
              <div className="mb-2">
                <label className="form-label fw-semibold">Amount (USD)</label>
                <input
                  type="number"
                  name="amount"
                  className="form-control"
                  value={formData.amount}
                  readOnly
                  required
                />
              </div>

              {/* Exchange Amount */}
              <div className="mb-2">
                <label className="form-label fw-semibold">
                  Exchange Amount ({selectedCurrencyCode})
                </label>
                <input
                  type="number"
                  name="fxAmount"
                  className="form-control"
                  value={formData.fxAmount}
                  onChange={handleChange}
                  placeholder="Enter exchange amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Status */}
              <div className="mb-2">
                <label className="form-label fw-semibold">Status</label>
                <div className="d-flex gap-3">
                  {["Active", "Inactive"].map((status) => (
                    <div className="form-check" key={status}>
                      <input
                        type="radio"
                        className="form-check-input"
                        name="status"
                        value={status}
                        id={`status-${status}`}
                        checked={formData.status === status}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`status-${status}`}
                      >
                        {status}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-grid mt-4">
                <button type="submit" className="btn btn-primary py-1 mb-3">
                  Add Exchange Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddGlobalExchangeModal;
